/**
 * Add product / Add variant keep a copy on this device until the server draft exists (owner decision
 * 2026-10-29: "save at any stage"). Reopening Add offers "Continue where you left off". The copy is
 * per studio user (and per parent product for a variant) and is dropped once the draft is saved.
 * Photo files are not copied here: the upload queue already keeps them in IndexedDB by mediaId.
 */

import { isPreviewVideo } from "../previewMedia";

const PREFIX = "hkp:addProductDraft:";
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

export type AddDraftSnapshot = {
  v: 1;
  savedAt: number;
  step: string;
  form: Record<string, unknown>;
  photos: any[];
};

export const addDraftKey = (ownerKey: string, variantOf?: string): string =>
  ownerKey ? `${PREFIX}${ownerKey}${variantOf ? `:variant:${variantOf}` : ""}` : "";

/** Files, blobs and blob: links do not survive a reload; everything else is plain JSON. */
const keepable = (_key: string, value: unknown) => {
  if (typeof Blob !== "undefined" && value instanceof Blob) return undefined;
  if (typeof value === "string" && value.startsWith("blob:")) return undefined;
  return value;
};

const photoRow = (row: any) => {
  if (!row || row._deleted) return null;
  const { blobImage, ...rest } = row;
  const keep = typeof blobImage === "string" && !blobImage.startsWith("blob:") ? { blobImage } : {};
  // A row with neither a stored link nor an upload job behind it is a preview we can't bring back.
  if (!keep.blobImage && !rest.mediaURL && !rest.mediaUrl && !rest.mediaId && !rest.mediaAssetId) return null;
  return { ...rest, ...keep };
};

export const hasDraftContent = (snap: AddDraftSnapshot | null): boolean => {
  if (!snap) return false;
  const form = snap.form || {};
  const text = (k: string) => String((form as any)[k] ?? "").trim().length > 0;
  return text("productName") || text("variantCode") || text("description") || Boolean((form as any).artform) || snap.photos.length > 0;
};

export const buildAddDraft = (form: any, photos: any[], step: string, now = Date.now()): AddDraftSnapshot => ({
  v: 1,
  savedAt: now,
  step,
  form: JSON.parse(JSON.stringify(form || {}, keepable)),
  photos: JSON.parse(JSON.stringify((photos || []).map(photoRow).filter(Boolean), keepable)),
});

export const saveAddDraft = (key: string, snap: AddDraftSnapshot): void => {
  if (!key) return;
  try {
    if (hasDraftContent(snap)) localStorage.setItem(key, JSON.stringify(snap));
    else localStorage.removeItem(key);
  } catch {
    // Storage full or blocked (private mode): the server draft still saves from Basics.
  }
};

export const loadAddDraft = (key: string, now = Date.now()): AddDraftSnapshot | null => {
  if (!key) return null;
  try {
    const snap = JSON.parse(localStorage.getItem(key) || "null") as AddDraftSnapshot | null;
    if (!snap || snap.v !== 1 || !snap.form || !Array.isArray(snap.photos)) return null;
    if (now - Number(snap.savedAt || 0) > MAX_AGE_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return hasDraftContent(snap) ? snap : null;
  } catch {
    return null;
  }
};

export const clearAddDraft = (key: string): void => {
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
};

/** Logout: a shared shop phone must not keep anyone's half-filled piece (prices included). */
export const clearAllAddDrafts = (): void => {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(PREFIX))
      .forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }
};

/** "2 photos · 1 video" ("" when there is nothing). */
export const mediaCountWords = (rows: any[]): string => {
  const videos = (rows || []).filter((row) => isPreviewVideo(row)).length;
  const photos = (rows || []).length - videos;
  const n = (count: number, word: string) => (count ? `${count} ${word}${count === 1 ? "" : "s"}` : "");
  return [n(photos, "photo"), n(videos, "video")].filter(Boolean).join(" · ");
};

/** "just now", "12 min ago", "3 h ago", "2 days ago" */
export const savedAgo = (savedAt: number, now = Date.now()): string => {
  const min = Math.max(0, Math.floor((now - savedAt) / 60000));
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h ago`;
  const d = Math.round(h / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
};
